/**
 * Design-Optionen für den Design-Drawer (Issue #3 Paket B).
 *
 * EINE Quelle für Client (DesignDrawer, applyBrandOverrides, HeroSlot) und
 * Server (functions/api/admin/design.js). Bewusst reines ES-Modul ohne
 * Browser- oder Node-APIs, damit Pages Functions und Vite es gleichermaßen
 * importieren können.
 */

// --- Plattform-Standards (ASL-CI) -------------------------------------------
// "Zurücksetzen" im Drawer stellt diese Werte wieder her. Sie spiegeln den
// ASL-Demo-Seed (Signalrot / Anthrazit) und den CSS-Fallback in index.css.
export const DEFAULT_PRIMARY_COLOR = '#e3000b';
export const DEFAULT_SECONDARY_COLOR = '#181716';
export const DEFAULT_FONT_ID = 'hanken';

// --- Schriften ----------------------------------------------------------------
// Kuratierte Liste statt Freitext: (1) CSS-Injection-Schutz — nur diese IDs
// erreichen je ein style-Attribut, (2) DSGVO — alle Dateien liegen self-hosted
// unter /fonts (OFL, siehe public/fonts/LICENSE.txt), kein Google-CDN-Abruf.
// Jede Schrift mit `file` braucht eine @font-face-Regel in src/index.css
// (tests/design-options.test.ts prüft Datei + Regel).
const SANS_TAIL = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const SERIF_TAIL = 'Georgia, "Times New Roman", serif';

export const FONT_OPTIONS = Object.freeze([
  {
    id: 'hanken',
    label: 'Hanken Grotesk',
    hint: 'Modern und klar',
    family: 'Hanken Grotesk',
    stack: `"Hanken Grotesk", ${SANS_TAIL}`,
    file: 'hanken-latin-wght.woff2',
    italicFile: 'hanken-latin-wght-italic.woff2',
    weights: '100 900',
  },
  {
    id: 'inter',
    label: 'Inter',
    hint: 'Neutral, sehr gut lesbar',
    family: 'Inter',
    stack: `"Inter", ${SANS_TAIL}`,
    file: 'inter-latin-wght.woff2',
    weights: '100 900',
  },
  {
    id: 'source-sans',
    label: 'Source Sans 3',
    hint: 'Sachlich, technisch',
    family: 'Source Sans 3',
    stack: `"Source Sans 3", ${SANS_TAIL}`,
    file: 'source-sans-latin-wght.woff2',
    weights: '200 900',
  },
  {
    id: 'open-sans',
    label: 'Open Sans',
    hint: 'Vertraut und freundlich',
    family: 'Open Sans',
    stack: `"Open Sans", ${SANS_TAIL}`,
    file: 'open-sans-latin-wght.woff2',
    weights: '300 800',
  },
  {
    id: 'ibm-plex',
    label: 'IBM Plex Sans',
    hint: 'Ingenieurs-Charakter',
    family: 'IBM Plex Sans',
    stack: `"IBM Plex Sans", ${SANS_TAIL}`,
    file: 'ibm-plex-latin-wght.woff2',
    weights: '100 700',
  },
  {
    id: 'manrope',
    label: 'Manrope',
    hint: 'Modern, geometrisch',
    family: 'Manrope',
    stack: `"Manrope", ${SANS_TAIL}`,
    file: 'manrope-latin-wght.woff2',
    weights: '200 800',
  },
  {
    id: 'montserrat',
    label: 'Montserrat',
    hint: 'Kräftig, starke Überschriften',
    family: 'Montserrat',
    stack: `"Montserrat", ${SANS_TAIL}`,
    file: 'montserrat-latin-wght.woff2',
    weights: '100 900',
  },
  {
    id: 'merriweather',
    label: 'Merriweather',
    hint: 'Serifenschrift, klassisch',
    family: 'Merriweather',
    stack: `"Merriweather", ${SERIF_TAIL}`,
    file: 'merriweather-latin-wght.woff2',
    weights: '300 900',
  },
  {
    id: 'system',
    label: 'Systemschrift',
    hint: 'Schnellste Ladezeit, wie das Gerät',
    family: 'system-ui',
    stack: `system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`,
    file: null,
    weights: null,
  },
]);

export function getFontOption(id) {
  if (typeof id !== 'string') return null;
  return FONT_OPTIONS.find((font) => font.id === id) || null;
}

/**
 * Löst gespeicherte Werte zur Schrift-ID auf. Akzeptiert ID, exakten Stack
 * und Altwerte wie 'Inter, sans-serif' (Treffer über die erste Familie).
 * Unbekannt -> null (der Aufrufer fällt dann auf den Standard zurück).
 */
export function fontIdFromValue(value) {
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  if (!raw) return null;
  const byId = getFontOption(raw);
  if (byId) return byId.id;
  const byStack = FONT_OPTIONS.find((font) => font.stack === raw);
  if (byStack) return byStack.id;
  const firstFamily = raw.split(',')[0].replace(/["']/g, '').trim().toLowerCase();
  const byFamily = FONT_OPTIONS.find((font) => font.family.toLowerCase() === firstFamily);
  return byFamily ? byFamily.id : null;
}

/** CSS-Stack zu einem gespeicherten Wert, oder null bei unbekanntem Wert. */
export function resolveFontStack(value) {
  const id = fontIdFromValue(value);
  return id ? getFontOption(id).stack : null;
}

// --- @font-face-Generator -------------------------------------------------------
// src/fonts.css ist AUTO-GENERIERT (npm run gen:fonts) und wird von
// tests/design-options.test.ts gegen diese Funktion geprüft — Schriftliste und
// CSS können so nicht auseinanderlaufen. Latin-Subset deckt Deutsch, €, „“ ab.
const LATIN_UNICODE_RANGE =
  'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, ' +
  'U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD';

export function buildFontFaceCss(options = FONT_OPTIONS) {
  const blocks = [];
  for (const font of options) {
    if (!font.file) continue;
    const faces = [{ style: 'normal', file: font.file }];
    if (font.italicFile) faces.push({ style: 'italic', file: font.italicFile });
    for (const face of faces) {
      blocks.push(
        [
          `/* ${font.label}${face.style === 'italic' ? ' (Italic)' : ''} */`,
          '@font-face {',
          `  font-family: "${font.family}";`,
          `  font-style: ${face.style};`,
          `  font-weight: ${font.weights};`,
          '  font-display: swap;',
          `  src: url("/fonts/${face.file}") format("woff2");`,
          `  unicode-range: ${LATIN_UNICODE_RANGE};`,
          '}',
        ].join('\n'),
      );
    }
  }
  return (
    '/* AUTO-GENERIERT aus src/lib/design-options.js (npm run gen:fonts) — nicht von Hand ändern.\n' +
    '   Self-hosted, SIL OFL 1.1, Lizenztexte: public/fonts/LICENSE.txt */\n\n' +
    blocks.join('\n\n') +
    '\n'
  );
}

// --- Farben -------------------------------------------------------------------
export const PRIMARY_SWATCHES = Object.freeze([
  { value: '#e3000b', label: 'ASL Rot' },
  { value: '#c2410c', label: 'Orange' },
  { value: '#b45309', label: 'Bernstein' },
  { value: '#15803d', label: 'Grün' },
  { value: '#0e7490', label: 'Petrol' },
  { value: '#0056b3', label: 'Blau' },
  { value: '#6d28d9', label: 'Violett' },
  { value: '#181716', label: 'Anthrazit' },
]);

export const SECONDARY_SWATCHES = Object.freeze([
  { value: '#181716', label: 'Anthrazit' },
  { value: '#334155', label: 'Schiefer' },
  { value: '#475569', label: 'Stahlgrau' },
  { value: '#5f5e5f', label: 'Grau' },
  { value: '#0f172a', label: 'Nachtblau' },
  { value: '#14532d', label: 'Dunkelgrün' },
]);

/** '#RGB' / 'RRGGBB' / '#RRGGBB' -> '#rrggbb' (klein), sonst null. */
export function normalizeHexColor(value) {
  if (typeof value !== 'string') return null;
  let hex = value.trim().toLowerCase();
  if (hex.startsWith('#')) hex = hex.slice(1);
  if (/^[0-9a-f]{3}$/.test(hex)) {
    hex = hex.split('').map((char) => char + char).join('');
  }
  return /^[0-9a-f]{6}$/.test(hex) ? `#${hex}` : null;
}

/** WCAG 2.1 relative Luminanz, null bei ungültigem Hex. */
export function relativeLuminance(value) {
  const hex = normalizeHexColor(value);
  if (!hex) return null;
  const [r, g, b] = [1, 3, 5]
    .map((index) => parseInt(hex.slice(index, index + 2), 16) / 255)
    .map((channel) => (channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG-Kontrastverhältnis (1–21, auf 2 Stellen), null bei ungültigem Hex. */
export function contrastRatio(colorA, colorB) {
  const lumA = relativeLuminance(colorA);
  const lumB = relativeLuminance(colorB);
  if (lumA === null || lumB === null) return null;
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);
  return Math.round(((lighter + 0.05) / (darker + 0.05)) * 100) / 100;
}

/** 'good' (>= 4.5, Fließtext), 'fair' (>= 3, große Schrift/Flächen), 'low'. */
export function contrastGrade(ratio) {
  if (typeof ratio !== 'number' || Number.isNaN(ratio)) return 'low';
  if (ratio >= 4.5) return 'good';
  if (ratio >= 3) return 'fair';
  return 'low';
}

// --- Hintergrundbilder --------------------------------------------------------
// Jeder Slot ist ein Paar content_blocks (Bild + Stärke) einer Seite. Neue
// Slots (z. B. Kategorie-Banner) lassen sich hier ergänzen, ohne den Drawer
// anzufassen. Das Bild wird mit hellem Schleier dargestellt (Stärke = Deckkraft
// des Bildes), damit dunkle Texte bei jedem Foto lesbar bleiben.
export const BACKGROUND_SLOTS = Object.freeze([
  {
    id: 'hero',
    label: 'Startseite: Kopfbereich',
    page: 'home',
    imageKey: 'hero_background',
    strengthKey: 'hero_background_strength',
    defaultStrength: 35,
    minStrength: 10,
    maxStrength: 70,
  },
]);

export function getBackgroundSlot(id) {
  return BACKGROUND_SLOTS.find((slot) => slot.id === id) || null;
}

export function clampStrength(value, slot = BACKGROUND_SLOTS[0]) {
  const parsed = typeof value === 'number' ? value : parseInt(String(value ?? ''), 10);
  if (!Number.isFinite(parsed)) return slot.defaultStrength;
  return Math.min(slot.maxStrength, Math.max(slot.minStrength, Math.round(parsed)));
}

// Same-Origin-Pfad: genau ein führender Slash. Backslash, Whitespace und Steuerzeichen sind tabu:
// Browser lesen '/\\host' und '/<TAB>/host' wie '//host' (protokollrelativ = fremder Host).
const SAME_ORIGIN_PATH = /^\/(?![/\\])[^\s\\"'<>\u0000-\u001f\u007f]*$/;
const HTTPS_URL = /^https:\/\/[^\s"'<>\\]+$/i;

/**
 * Nur Same-Origin-Pfade und https-URLs dürfen als Hintergrund gerendert werden
 * (CMS-Werte sind tenant-editierbar; alles andere ist kein gültiges Bildziel).
 * Gilt für Client UND Server (functions/api/admin/content.js prüft image_url damit).
 */
export function safeImageUrl(value) {
  if (typeof value !== 'string') return null;
  const url = value.trim();
  if (!url || url.length > 2048) return null;
  if (SAME_ORIGIN_PATH.test(url)) return url;
  return HTTPS_URL.test(url) ? url : null;
}

/** Liest {imageUrl, strength} eines Slots aus der Content-Map (blocksToMap). */
export function readBackgroundSlot(contentMap, slot = BACKGROUND_SLOTS[0]) {
  const map = contentMap && typeof contentMap === 'object' ? contentMap : {};
  return {
    imageUrl: safeImageUrl(map[slot.imageKey]),
    strength: clampStrength(map[slot.strengthKey], slot),
  };
}

// --- Server-Validierung (PUT /api/admin/design) -------------------------------
export const DESIGN_FIELDS = Object.freeze(['primary_color', 'secondary_color', 'font_family', 'font_heading']);

const FIELD_LABELS = {
  primary_color: 'Hauptfarbe',
  secondary_color: 'Zweitfarbe',
  font_family: 'Schriftart',
  font_heading: 'Überschriften-Schrift',
};

/**
 * Prüft einen Design-Patch. Schlüssel fehlt = unverändert; null/'' = Reset.
 * Farben werden zu '#rrggbb' normalisiert, Schriften nur als bekannte ID
 * akzeptiert. Unbekannte Felder werden abgelehnt (kein stilles Schlucken).
 *
 * @returns {{ ok: boolean, errors: string[], patch: Record<string, string|null> }}
 */
export function validateDesignPatch(body) {
  const errors = [];
  const patch = {};

  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return { ok: false, errors: ['Ungültiger Request-Body.'], patch };
  }

  const unknown = [];
  for (const [key, raw] of Object.entries(body)) {
    if (key === 'tenant_id' || key === 'tenantId') continue;
    if (!DESIGN_FIELDS.includes(key)) {
      unknown.push(key);
      continue;
    }

    if (raw === null || raw === '') {
      patch[key] = null;
      continue;
    }

    if (key.endsWith('_color')) {
      const hex = normalizeHexColor(raw);
      if (hex) {
        patch[key] = hex;
      } else {
        errors.push(`${FIELD_LABELS[key]}: ungültiger Farbwert (erwartet #RRGGBB).`);
      }
      continue;
    }

    if (typeof raw === 'string' && getFontOption(raw)) {
      patch[key] = raw;
    } else {
      errors.push(`${FIELD_LABELS[key]}: unbekannte Schrift.`);
    }
  }

  if (unknown.length > 0) {
    errors.push(`Unbekannte Felder: ${unknown.slice(0, 5).join(', ')}.`);
  }
  if (errors.length === 0 && Object.keys(patch).length === 0) {
    errors.push('Keine Design-Änderung angegeben.');
  }

  return { ok: errors.length === 0, errors, patch };
}
